/**
 * Generic ordering and fuzzy search for selectable facets.
 *
 * Pure module:
 * - no Vue
 * - no Vuex
 * - no API client
 * - no components
 *
 * Ordering:
 *
 * Empty query:
 *   count desc -> stable hash -> raw id
 *
 * Search:
 *   relevance band -> coverage tier -> count desc -> stable hash -> raw id
 *
 * When counts are withheld:
 *
 * Empty query:
 *   stable hash -> raw id
 *
 * Search:
 *   relevance band -> coverage tier -> stable hash -> raw id
 */

export const BAND_EXACT = 0;
export const BAND_PREFIX = 1;
export const BAND_SUBSTRING = 2;
export const BAND_TYPO = 3;

export const BAND_LABELS = [
  'Exact',
  'Starts with',
  'Contains',
  'Did you mean',
];

export const FUZZY_MIN_QUERY_LENGTH = 4;
export const FUZZY_SHORT_QUERY_LENGTH = 4;
export const FUZZY_MAX_EDITS_SHORT = 1;
export const FUZZY_MAX_EDITS_LONG = 2;

export const SEARCH_DEBOUNCE_MS = 200;

export const COVERAGE_TIER_THRESHOLDS = [0.8, 0.4];

/**
 * Normalize a facet label before comparison.
 */
export function normalizeFacetLabel( value ) {
  return String(
    value === null || value === undefined
      ? ''
      : value,
  )
    .toLowerCase()
    .replace( /[^a-z0-9]+/g, ' ' )
    .trim();
}

/**
 * FNV-1a, 32-bit.
 *
 * Gives us a stable, non-alphabetical tie order.
 */
function fnv1a( value ) {
  let hash = 0x811c9dc5;
  const str = String( value );

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt( i );
    hash = Math.imul(
      hash,
      0x01000193,
    ) >>> 0;
  }

  return hash >>> 0;
}

/**
 * Stable tie order for a facet option.
 *
 * The scope keeps separate facets in separate deterministic namespaces.
 */
export function facetTieOrder(
  id,
  scope = 'facet',
) {
  return fnv1a(
    `${ scope }-order:${ id }`,
  );
}

/**
 * Capped Damerau-Levenshtein / Optimal String Alignment.
 *
 * Supports:
 * - insertion
 * - deletion
 * - replacement
 * - adjacent character transposition
 */
function editDistance( a, b, max ) {
  if (
    Math.abs( a.length - b.length ) >
    max
  ) {
    return max + 1;
  }

  let prev2 = null;
  let prev = new Array(
    b.length + 1,
  );
  let curr = new Array(
    b.length + 1,
  );

  for (
    let j = 0;
    j <= b.length;
    j++
  ) {
    prev[j] = j;
  }

  for (
    let i = 1;
    i <= a.length;
    i++
  ) {
    curr[0] = i;

    let best = curr[0];

    for (
      let j = 1;
      j <= b.length;
      j++
    ) {
      const cost =
        a.charCodeAt( i - 1 ) ===
        b.charCodeAt( j - 1 )
          ? 0
          : 1;

      let value = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost,
      );

      if (
        prev2 &&
        i > 1 &&
        j > 1 &&
        a.charCodeAt( i - 1 ) ===
        b.charCodeAt( j - 2 ) &&
        a.charCodeAt( i - 2 ) ===
        b.charCodeAt( j - 1 )
      ) {
        value = Math.min(
          value,
          prev2[j - 2] + 1,
        );
      }

      curr[j] = value;

      if (value < best) {
        best = value;
      }
    }

    if (best > max) {
      return max + 1;
    }

    prev2 = prev;
    prev = curr;
    curr = new Array(
      b.length + 1,
    );
  }

  return prev[b.length];
}

function sharesFirstCharacter(
  candidate,
  query,
) {
  return (
    candidate.length > 0 &&
    query.length > 0 &&
    candidate.charCodeAt( 0 ) ===
    query.charCodeAt( 0 )
  );
}

function maxEditsFor(
  queryLength,
  config,
) {
  return queryLength <=
  config.shortQueryLength
    ? config.maxEditsShort
    : config.maxEditsLong;
}

/**
 * Lower tier is better.
 */
function coverageTier(
  matchedCharacters,
  nameLength,
  thresholds,
) {
  const coverage =
    nameLength > 0
      ? matchedCharacters /
      nameLength
      : 0;

  for (
    let tier = 0;
    tier < thresholds.length;
    tier++
  ) {
    if (
      coverage >= thresholds[tier]
    ) {
      return tier;
    }
  }

  return thresholds.length;
}

function isTypoMatch(
  candidate,
  query,
  max,
  requireSameFirstCharacter,
) {
  if (
    requireSameFirstCharacter &&
    !sharesFirstCharacter(
      candidate,
      query,
    )
  ) {
    return false;
  }

  return (
    editDistance(
      candidate,
      query,
      max,
    ) <= max
  );
}

function matchFacetOption(
  prepared,
  query,
  config,
) {
  const nameLength =
    prepared.normalized.length;

  if (
    prepared.normalized === query
  ) {
    return {
      band: BAND_EXACT,
      matched: nameLength,
    };
  }

  if (
    prepared.normalized.startsWith(
      query,
    ) ||
    prepared.words.some( ( word ) =>
      word.startsWith( query ),
    )
  ) {
    return {
      band: BAND_PREFIX,
      matched: query.length,
    };
  }

  if (
    prepared.normalized.includes(
      query,
    )
  ) {
    return {
      band: BAND_SUBSTRING,
      matched: query.length,
    };
  }

  if (
    !config.fuzzy ||
    query.length <
    config.minFuzzyLength
  ) {
    return null;
  }

  const max = maxEditsFor(
    query.length,
    config,
  );

  if (
    isTypoMatch(
      prepared.normalized,
      query,
      max,
      config.requireSameFirstCharacter,
    )
  ) {
    return {
      band: BAND_TYPO,
      matched: nameLength,
    };
  }

  const matchingWord =
    prepared.words.find( ( word ) =>
      isTypoMatch(
        word,
        query,
        max,
        config.requireSameFirstCharacter,
      ),
    );

  if (matchingWord) {
    return {
      band: BAND_TYPO,
      matched:
      matchingWord.length,
    };
  }

  return null;
}

function knownCount( value ) {
  return (
    typeof value === 'number' &&
    isFinite( value )
  )
    ? value
    : null;
}

function compareRawId( a, b ) {
  if (
    typeof a === 'number' &&
    typeof b === 'number'
  ) {
    return a - b;
  }

  const left = String( a );
  const right = String( b );

  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }

  return 0;
}

function resolveConfig( config = {} ) {
  return {
    scope:
      config.scope ||
      'facet',

    getId:
      config.getId ||
      ( ( option ) =>
        option?.id ),

    getLabel:
      config.getLabel ||
      ( ( option ) =>
        option?.name ??
        option?.label ??
        '' ),

    getCount:
      config.getCount ||
      ( ( option ) =>
        option?.count ),

    getStatus:
      config.getStatus ||
      ( ( option ) =>
        option?.status ),

    unavailableStatus:
      config.unavailableStatus ||
      'unavailable',

    fuzzy:
      config.fuzzy !== false,

    minFuzzyLength:
      config.minFuzzyLength ??
      FUZZY_MIN_QUERY_LENGTH,

    shortQueryLength:
      config.shortQueryLength ??
      FUZZY_SHORT_QUERY_LENGTH,

    maxEditsShort:
      config.maxEditsShort ??
      FUZZY_MAX_EDITS_SHORT,

    maxEditsLong:
      config.maxEditsLong ??
      FUZZY_MAX_EDITS_LONG,

    requireSameFirstCharacter:
      config.requireSameFirstCharacter !==
      false,

    coverageThresholds:
      config.coverageThresholds ||
      COVERAGE_TIER_THRESHOLDS,
  };
}

function prepareFacetOption(
  option,
  config,
) {
  const id =
    config.getId( option );

  const label =
    config.getLabel( option );

  const normalized =
    normalizeFacetLabel( label );

  return Object.freeze( {
    raw: option,
    id,
    label,
    count:
      config.getCount( option ),
    status:
      config.getStatus( option ),

    normalized,

    words: Object.freeze(
      normalized
        .split( ' ' )
        .filter( Boolean ),
    ),

    tie: facetTieOrder(
      id,
      config.scope,
    ),

    __facetPrepared: true,
  } );
}

/**
 * Precompute comparison keys for a list of options.
 *
 * Useful when the same option list will be searched repeatedly.
 */
export function prepareFacetOptions(
  options = [],
  config = {},
) {
  const resolved =
    resolveConfig( config );

  return Object.freeze(
    ( options || [] ).map( ( option ) =>
      prepareFacetOption(
        option,
        resolved,
      ),
    ),
  );
}

function selectedValueId(
  value,
  config,
) {
  if (
    value !== null &&
    typeof value === 'object'
  ) {
    const id =
      config.getId( value );

    if (
      id !== null &&
      id !== undefined
    ) {
      return id;
    }
  }

  return value;
}

/**
 * Rank unselected, available facet options.
 */
export function rankFacetOptions( {
                                    options = [],
                                    query = '',
                                    selectedIds = [],
                                    countsWithheld = false,
                                    config = {},
                                  } = {} ) {
  const resolved =
    resolveConfig( config );

  const normalizedQuery =
    normalizeFacetLabel( query );

  const selected = new Set(
    ( selectedIds || [] ).map(
      ( value ) =>
        selectedValueId(
          value,
          resolved,
        ),
    ),
  );

  const results = [];

  ( options || [] ).forEach(
    ( row ) => {
      const prepared =
        row?.__facetPrepared === true
          ? row
          : prepareFacetOption(
            row,
            resolved,
          );

      if (
        selected.has(
          prepared.id,
        )
      ) {
        return;
      }

      if (
        prepared.status ===
        resolved.unavailableStatus
      ) {
        return;
      }

      const count =
        knownCount(
          prepared.count,
        );

      if (
        count !== null &&
        count <= 0
      ) {
        return;
      }

      if (!normalizedQuery) {
        results.push( {
          option: prepared.raw,
          prepared,
          band: null,
          bandLabel: null,
          tier: null,
        } );

        return;
      }

      const hit =
        matchFacetOption(
          prepared,
          normalizedQuery,
          resolved,
        );

      if (!hit) {
        return;
      }

      results.push( {
        option: prepared.raw,
        prepared,
        band: hit.band,
        bandLabel:
          BAND_LABELS[hit.band],

        tier: coverageTier(
          hit.matched,
          prepared.normalized.length,
          resolved.coverageThresholds,
        ),
      } );
    },
  );

  results.sort( ( a, b ) => {
    if (
      normalizedQuery &&
      a.band !== b.band
    ) {
      return (
        a.band - b.band
      );
    }

    if (
      normalizedQuery &&
      a.tier !== b.tier
    ) {
      return (
        a.tier - b.tier
      );
    }

    if (!countsWithheld) {
      const countA =
        knownCount(
          a.prepared.count,
        );

      const countB =
        knownCount(
          b.prepared.count,
        );

      if (
        countA !== null &&
        countB !== null &&
        countA !== countB
      ) {
        return (
          countB - countA
        );
      }
    }

    if (
      a.prepared.tie !==
      b.prepared.tie
    ) {
      return (
        a.prepared.tie -
        b.prepared.tie
      );
    }

    return compareRawId(
      a.prepared.id,
      b.prepared.id,
    );
  } );

  return results;
}
